import React, { useMemo, useState } from 'react'
import { formatEur, formatRange, computePricePositions, computeOpportunities } from './networkUtils'

const OPP_STYLES = {
  negotiation: { label: 'Negotiation', icon: '💰', color: '#dc2626', bg: 'bg-red-50', border: 'border-red-200' },
  sole_source: { label: 'Sole-Source Risk', icon: '⚠️', color: '#d97706', bg: 'bg-amber-50', border: 'border-amber-200' },
  consolidation: { label: 'Consolidation', icon: '🔄', color: '#7c3aed', bg: 'bg-purple-50', border: 'border-purple-200' },
  quick_win: { label: 'Quick Win', icon: '⚡', color: '#059669', bg: 'bg-green-50', border: 'border-green-200' },
}

export default function OpportunitiesView({ parts, stats }) {
  const [expandedOpp, setExpandedOpp] = useState(null)

  // Compute price positions then opportunities
  const enrichedParts = useMemo(() => computePricePositions(parts), [parts])
  const opportunities = useMemo(() => computeOpportunities(enrichedParts), [enrichedParts])

  // Aggregate KPIs
  const totalSavings = opportunities.reduce((s, o) => s + o.estSavings, 0)
  const highConf = opportunities.filter(o => o.confidence === 'High')
  const highConfSavings = highConf.reduce((s, o) => s + o.estSavings, 0)
  const totalSpend = stats?.total_spend || 0

  // Type breakdown
  const byType = {}
  opportunities.forEach(o => {
    if (!byType[o.type]) byType[o.type] = { count: 0, savings: 0 }
    byType[o.type].count++
    byType[o.type].savings += o.estSavings
  })

  if (opportunities.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <p className="text-lg mb-2">No opportunities detected</p>
        <p className="text-sm">Upload purchase history with pricing to enable savings analysis</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Executive summary hero */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-white to-gray-50">
          <div className="flex gap-8 flex-wrap">
            <div className="flex-1 min-w-[300px]">
              <h2 className="text-xl font-bold text-gray-800 mb-1">Savings Opportunities</h2>
              <p className="text-xs text-gray-500 mb-5">
                Algorithmically identified from price benchmarking, supply risk, and sourcing structure across{' '}
                <strong>{stats?.total_parts || 0} parts</strong> and <strong>{formatEur(totalSpend)}</strong> total spend.
              </p>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="text-lg font-bold text-green-600">{formatRange(totalSavings)}</div>
                  <div className="text-[10px] font-semibold text-gray-500 uppercase mt-1">Potential Annual Savings</div>
                  <div className="text-[10px] text-gray-400">{opportunities.length} opportunities</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="text-lg font-bold text-green-600">{formatRange(highConfSavings)}</div>
                  <div className="text-[10px] font-semibold text-gray-500 uppercase mt-1">High-Confidence</div>
                  <div className="text-[10px] text-gray-400">{highConf.length} opportunities</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="text-lg font-bold text-[#1e5045]">{formatEur(totalSpend)}</div>
                  <div className="text-[10px] font-semibold text-gray-500 uppercase mt-1">Total Spend</div>
                  <div className="text-[10px] text-gray-400">addressable</div>
                </div>
              </div>
            </div>

            {/* Type breakdown */}
            <div className="w-64 flex-shrink-0">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-3">By Category</div>
              <div className="space-y-2">
                {Object.entries(byType).map(([type, data]) => {
                  const style = OPP_STYLES[type]
                  const pct = totalSavings > 0 ? (data.savings / totalSavings * 100) : 0
                  return (
                    <div key={type} className="flex items-center gap-2">
                      <span className="text-sm">{style.icon}</span>
                      <div className="flex-1">
                        <div className="flex justify-between text-[11px]">
                          <span className="text-gray-600">{style.label}</span>
                          <span className="font-medium" style={{ color: style.color }}>{formatRange(data.savings)}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full mt-0.5 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: style.color }} />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Confidence distribution */}
              <div className="mt-4">
                <div className="text-[10px] text-gray-400 uppercase mb-1">Confidence</div>
                <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
                  <div className="bg-green-500" style={{ width: `${highConf.length / opportunities.length * 100}%` }} />
                  <div className="bg-amber-500" style={{ width: `${opportunities.filter(o => o.confidence === 'Medium').length / opportunities.length * 100}%` }} />
                  <div className="bg-red-500" style={{ width: `${opportunities.filter(o => o.confidence === 'Low').length / opportunities.length * 100}%` }} />
                </div>
                <div className="flex gap-3 mt-1 text-[10px] text-gray-400">
                  <span className="text-green-600">High: {highConf.length}</span>
                  <span className="text-amber-600">Med: {opportunities.filter(o => o.confidence === 'Medium').length}</span>
                  <span className="text-red-600">Low: {opportunities.filter(o => o.confidence === 'Low').length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Opportunity cards */}
      <div className="space-y-3">
        {opportunities.map((opp, i) => {
          const style = OPP_STYLES[opp.type]
          const isExpanded = expandedOpp === i
          return (
            <div
              key={i}
              className={`bg-white rounded-lg border ${style.border} shadow-sm overflow-hidden cursor-pointer transition-all hover:shadow-md`}
              onClick={() => setExpandedOpp(isExpanded ? null : i)}
            >
              <div className="px-4 py-3 flex items-center gap-3">
                <span className="text-lg">{style.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: style.color + '15', color: style.color }}>
                      {style.label}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      opp.confidence === 'High' ? 'bg-green-100 text-green-700' :
                      opp.confidence === 'Medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>{opp.confidence}</span>
                  </div>
                  <div className="text-sm font-medium text-gray-800 mt-1 truncate">{opp.title}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-bold text-green-600">{formatRange(opp.estSavings)}</div>
                  <div className="text-[10px] text-gray-400">
                    {opp.parts?.length || 0} parts · {formatEur(opp.spendExposed)} exposed
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className={`px-4 py-3 border-t ${style.border} ${style.bg}`}>
                  <p className="text-xs text-gray-600 leading-relaxed">{opp.detail}</p>
                  {opp.parts && opp.parts.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Affected Parts</div>
                      <div className="flex flex-wrap gap-1">
                        {opp.parts.slice(0, 8).map(p => (
                          <span key={p.pn} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono text-gray-700">
                            {p.pn}{p.description ? <span className="font-sans text-gray-400 ml-1">{p.description}</span> : null}
                          </span>
                        ))}
                        {opp.parts.length > 8 && (
                          <span className="px-2 py-0.5 text-[10px] text-gray-400">+{opp.parts.length - 8} more</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
